import { useTranslation } from "react-i18next";
import { Dumbbell, MapPin } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/shared/components/ui/sheet";
import { Separator } from "@/shared/components/ui/separator";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Button } from "@/shared/components/ui/button";
import { VenueTimeBadge } from "./VenueTimeBadge";
import { VenuePhotoCarousel } from "./VenuePhotoCarousel";
import { VenueHoursTable } from "./VenueHoursTable";
import { venueTypeIcon } from "./venue-icons";
import type { PendingVenue } from "./types";

interface VenueDetailDrawerProps {
  readonly venue: PendingVenue | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onApprove: (venue: PendingVenue) => void;
  readonly onReject: (venue: PendingVenue) => void;
  readonly onRequestChanges: (venue: PendingVenue) => void;
}

function VenueDetailContent({
  venue,
  onApprove,
  onReject,
  onRequestChanges,
}: {
  readonly venue: PendingVenue;
  readonly onApprove: (venue: PendingVenue) => void;
  readonly onReject: (venue: PendingVenue) => void;
  readonly onRequestChanges: (venue: PendingVenue) => void;
}) {
  const { t } = useTranslation();
  const TypeIcon = venueTypeIcon[venue.type] ?? Dumbbell;

  return (
    <>
      <SheetHeader className="pr-8">
        <SheetTitle className="text-xl">{venue.name}</SheetTitle>
        <SheetDescription className="flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5" />
          {venue.address}, {venue.city}, {venue.state}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-6 space-y-6">
        <div className="flex items-center gap-3">
          <VenueTimeBadge venueId={venue.id} createdAt={venue.createdAt} />
          <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 font-sans text-xs font-medium text-muted-foreground">
            <TypeIcon className="h-3 w-3" />
            {t(`venues.type.${venue.type}`)}
          </span>
        </div>

        <VenuePhotoCarousel key={venue.id} photos={venue.photos} venueName={venue.name} />

        {venue.description && (
          <div>
            <h4 className="mb-1 font-sans text-sm font-semibold text-foreground">
              {t("venues.detail.description")}
            </h4>
            <p className="font-sans text-sm font-normal text-muted-foreground">
              {venue.description}
            </p>
          </div>
        )}

        <Separator />

        {venue.amenities.length > 0 && (
          <div>
            <h4 className="mb-2 font-sans text-sm font-semibold text-foreground">
              {t("venues.detail.amenities")}
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {venue.amenities.map((a) => (
                <span
                  key={a.id}
                  className="rounded-full bg-muted px-2.5 py-0.5 font-sans text-xs font-normal text-muted-foreground"
                >
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {venue.hours && venue.hours.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="mb-2 font-sans text-sm font-semibold text-foreground">
                {t("venues.detail.hours")}
              </h4>
              <VenueHoursTable hours={venue.hours} />
            </div>
          </>
        )}

        <Separator />

        <div className="space-y-2">
          <div className="flex gap-3">
            <Button
              data-testid="approve-venue-button"
              className="flex-1"
              onClick={() => onApprove(venue)}
            >
              {t("venues.actions.approve")}
            </Button>
            <Button
              data-testid="reject-venue-button"
              variant="outline"
              className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
              onClick={() => onReject(venue)}
            >
              {t("venues.actions.reject")}
            </Button>
          </div>
          <Button
            data-testid="request-changes-button"
            variant="secondary"
            className="w-full"
            onClick={() => onRequestChanges(venue)}
          >
            {t("venues.actions.requestChanges")}
          </Button>
        </div>
      </div>
    </>
  );
}

export function VenueDetailDrawer({
  venue,
  open,
  onOpenChange,
  onApprove,
  onReject,
  onRequestChanges,
}: VenueDetailDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg" data-testid="venue-detail-drawer">
        {venue ? (
          <VenueDetailContent
            venue={venue}
            onApprove={onApprove}
            onReject={onReject}
            onRequestChanges={onRequestChanges}
          />
        ) : (
          <div className="space-y-4 pt-6">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
